import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Typography,
  CircularProgress
} from '@mui/material';
import { Save, Trash } from 'lucide-react';
import { FilterFieldSx, CSS_PRIMARY } from '../../../../constants/style';
import { saveOrgType, deleteOrgType } from '../../../../apis/Masters/GlobalType';
import ConfirmDialog from '../../common/ConfirmDialog';

/* ── Empty form state ─────────────────────────────────────────────────────── */
const emptyForm = {
  globalTypeName: '',
  sortOrder: '',
  isActive: true,
  globalTypeID: 0,
  globalTypeCategoryID: 0
};

/* ── Validation ───────────────────────────────────────────────────────────── */
const validate = (form) => {
  const errors = {};

  if (!form.globalTypeName.trim()) errors.globalTypeName = 'Organization Type Name is required.';

  if (!String(form.sortOrder).trim()) errors.sortOrder = 'Sort Order is required.';

  return errors;
};

const AddOrgType = ({
  masterData = null,
  userData = null,
  userAccess = null,
  onRefresh,
  onClose
}) => {
  const isEditMode = Boolean(userData);

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isDeleteAllowd = isEditMode ? userAccess?.hasDeletePermission : false;
  const isEditAllowed = isEditMode ? userAccess?.hasEditPermission : userAccess?.hasAddPermission;

  // ── Confirm-dialog state ────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: null
  });

  const openConfirm = ({ title, content, onConfirm }) =>
    setConfirmDialog({ open: true, title, content, onConfirm });

  const closeConfirm = () =>
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));

  const deleteHandler = () => {
    if (!userAccess?.hasDeletePermission) return;
    openConfirm({
      title: `Delete ${masterData?.title} Record`,
      content: `Are you sure you want to delete the ${masterData?.title} record? This action cannot be undone.`,
      onConfirm: () => {
        closeConfirm();
        handleDelete();
      }
    });
  };

  useEffect(() => {
    if (userData) {
      setForm({
        globalTypeName: userData.globalTypeName || '',
        sortOrder: userData.sortOrder || '',
        isActive:
          userData.status === 'Active' || userData.isActive !== undefined
            ? userData.isActive ?? userData.status === 'Active'
            : true
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [userData]);

  // ── Field change ───────────────────────────────────────────────────────────
  const handleChange = (field) => (e) => {
    const value =
      e.target.type === 'checkbox' ? e.target.checked : (e.target.value || '');
    const next = { ...form, [field]: value };
    setForm(next);
    // Live-validate touched fields
    if (touched[field]) {
      const errs = validate(next);
      setErrors((prev) => ({ ...prev, [field]: errs[field] }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errs = validate(form);
    setErrors((prev) => ({ ...prev, [field]: errs[field] }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Touch all fields to reveal errors
    const allTouched = Object.fromEntries(Object.keys(form).map((k) => [k, true]));
    setTouched(allTouched);

    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      const payload = {
        ...(isEditMode ? { id: userData.id } : {}),
        globalTypeName: (form.globalTypeName.trim()).toUpperCase(),
        sortOrder: form.sortOrder,
        isActive: form.isActive,
        globalTypeID: userData?.globalTypeID || 0,
        globalTypeCategoryID: masterData?.id || 0
      };
      const successMsg = payload.globalTypeID
        ? `${masterData?.title} updated successfully`
        : `${masterData?.title} added successfully`;
      const response = await saveOrgType(payload, successMsg, '');
      if (response && response.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to save global type:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const successMsg = `${masterData.title} deleted successfully`;
      const response = await deleteOrgType(userData?.globalTypeID, masterData?.id, successMsg, '');
      if (response && response.success) {
        onClose?.();
        onRefresh?.();
      }
    } catch (err) {
      console.log('Failed to delete global type:', err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Has any visible error ──────────────────────────────────────────────────
  const hasErrors = Object.keys(validate(form)).length > 0;

  return (
    <>
      <Box component="form" noValidate autoComplete="off">
        <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 2.5 }}>
          {/* Name */}
          <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
            <TextField
              fullWidth
              label={masterData?.title}
              placeholder={`e.g. ${masterData?.title}`}
              value={form.globalTypeName}
              onChange={handleChange('globalTypeName')}
              onBlur={handleBlur('globalTypeName')}
              error={touched.globalTypeName && Boolean(errors.globalTypeName)}
              helperText={touched.globalTypeName && errors.globalTypeName}
              size="small"
              sx={{
                ...FilterFieldSx,
                '& input': {
                  textTransform: 'uppercase'
                }
              }}
              slotProps={{
                input: {
                  readOnly: !isEditAllowed
                }
              }}
            />
          </Box>

          {/* Sort Order */}
          <Box sx={{ flex: '1 1 50%', minWidth: '200px' }}>
            <TextField
              fullWidth
              label="Sort Order"
              type="number"
              placeholder="e.g. 1"
              value={form.sortOrder}
              onChange={handleChange('sortOrder')}
              onBlur={handleBlur('sortOrder')}
              error={touched.sortOrder && Boolean(errors.sortOrder)}
              helperText={touched.sortOrder && errors.sortOrder}
              size="small"
              sx={FilterFieldSx}
              slotProps={{
                input: {
                  readOnly: !isEditAllowed
                }
              }}
            />
          </Box>

          {/* isActive */}
          <Box sx={{ flex: '1 1 50%', minWidth: '200px', display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.isActive}
                  onChange={handleChange('isActive')}
                  size="small"
                  sx={{
                    color: 'primary.main',
                    '&.Mui-checked': { color: CSS_PRIMARY }
                  }}
                  readOnly={!isEditAllowed}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Active
                </Typography>
              }
            />
          </Box>
        </Box>
        {/* ── Actions ─────────────────────────────────────────────────────── */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={
              deleting ? <CircularProgress size={14} color="inherit" /> : <Trash size={15} />
            }
            disabled={deleting}
            onClick={deleteHandler}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: '#e05757',
              '&:hover': { background: '#6d6464' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: isDeleteAllowd ? 'inline-flex' : 'none'
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save size={15} />}
            disabled={saving || (hasErrors && Object.keys(touched).length > 0)}
            onClick={handleSave}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              minWidth: 100,
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              boxShadow: '0 4px 12px rgba(46,204,113,0.3)',
              display: isEditAllowed ? 'inline-flex' : 'none'
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>

      {/* ── Confirm Dialog ─────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        content={confirmDialog.content}
        titleIcon={<Trash size={18} />}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </>
  );
};

export default AddOrgType;
